import type { AppEventProto } from "@noom/noom-contracts/noom_contracts/events/app/app_event";

import { trackRumNativeBridgeError } from "@app/rumMonitoring";

import { getIOSAnalyticsMessageHandler } from "../../utils/api/messageHandlers";

const logPrefix = "[NativeAnalyticsService]";

type BridgeResult = void | Promise<unknown>;
type IOSBridgeResponseTuple = [unknown, string | null | undefined];

type AndroidSendAppEvent = (
  encodedRequest: string,
  requestId: string
) => BridgeResult;

type AndroidNativeAnalyticsService = {
  sendAppEvent: AndroidSendAppEvent;
};

declare global {
  interface Window {
    /**
     * Android codegen AppJsBridge — `sendAppEvent(encoded, requestId)`.
     */
    NativeAnalyticsService?: AndroidNativeAnalyticsService;
    /** Optional Android alias used by some hosts. */
    NativeAnalytics?: {
      sendAppEvent?: AndroidSendAppEvent;
    };
  }
}

function encodePayload(payload: AppEventProto): string {
  return JSON.stringify(payload);
}

function createRequestId(): string {
  return window.crypto?.randomUUID?.() ?? `native-analytics-${Date.now()}`;
}

function isIOSBridgeResponseTuple(
  value: unknown
): value is IOSBridgeResponseTuple {
  return Array.isArray(value) && value.length === 2;
}

async function handleBridgeResult(
  result: BridgeResult,
  bridgeName: string,
  payload: AppEventProto
): Promise<boolean> {
  try {
    const resolvedResult = await result;

    if (isIOSBridgeResponseTuple(resolvedResult) && resolvedResult[1]) {
      console.error(`${logPrefix} ${bridgeName} returned error`, {
        error: resolvedResult[1],
        payload,
      });

      trackRumNativeBridgeError({ bridge: bridgeName });

      return false;
    }

    console.info(`${logPrefix} ${bridgeName} dispatched`, payload);
    return true;
  } catch (error) {
    console.error(`${logPrefix} ${bridgeName} rejected`, {
      error,
      payload,
    });

    trackRumNativeBridgeError({ bridge: bridgeName });

    return false;
  }
}

/**
 * Android WebViews inject `window.NativeAnalyticsService` via
 * `@JavascriptInterface` with `sendAppEvent(encoded, requestId)`. Care tab
 * does not always inject `window.Android`, so UA is the reliable signal.
 * iOS uses `webkit.messageHandlers`, not a window-level service object.
 */
function isAndroidWebViewHost(): boolean {
  return /Android/i.test(window.navigator.userAgent);
}

/**
 * Android `@JavascriptInterface` methods must be invoked on the injected
 * host object. Extracting `host.sendAppEvent` and calling it unbound throws:
 * "Java bridge method can't be invoked on a non-injected object".
 */
function getAndroidSendAppEvent(): AndroidSendAppEvent | undefined {
  if (!isAndroidWebViewHost()) {
    return undefined;
  }

  const aliasHost = window.NativeAnalytics;
  if (typeof aliasHost?.sendAppEvent === "function") {
    return (encodedRequest, requestId) =>
      aliasHost.sendAppEvent!(encodedRequest, requestId);
  }

  const serviceHost = window.NativeAnalyticsService;
  if (typeof serviceHost?.sendAppEvent === "function") {
    return (encodedRequest, requestId) =>
      serviceHost.sendAppEvent(encodedRequest, requestId);
  }

  return undefined;
}

/**
 * Sends an {@link AppEventProto} to the embedding native app over the
 * first available JS bridge (Android or iOS WebKit). Populate exactly one
 * discriminator field on the payload (e.g. `medPageButtonClicked`).
 *
 * Never throws — returns `true` on successful dispatch, `false` when no
 * bridge is present or the bridge errored. Feature code should call a
 * domain helper from `medAnalytics.ts` instead of using this directly.
 */
export async function sendAppEvent(
  payload: AppEventProto
): Promise<boolean> {
  try {
    const androidSendAppEvent = getAndroidSendAppEvent();
    if (androidSendAppEvent) {
      return handleBridgeResult(
        androidSendAppEvent(encodePayload(payload), createRequestId()),
        "Android NativeAnalyticsService.sendAppEvent",
        payload
      );
    }

    const iosHandler = getIOSAnalyticsMessageHandler();
    if (iosHandler?.postMessage) {
      return handleBridgeResult(
        iosHandler.postMessage(encodePayload(payload)),
        "iOS NativeAnalyticsService.sendAppEvent",
        payload
      );
    }

    console.warn(`${logPrefix} bridge unavailable`, { payload });

    return false;
  } catch (error) {
    console.error(`${logPrefix} sendAppEvent failed`, {
      error,
      payload,
    });

    trackRumNativeBridgeError({ bridge: "sendAppEvent" });

    return false;
  }
}
